import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Lock } from "lucide-react";
import { SITE } from "@/lib/site";

const LINKS = [
  { to: "/fordon", label: "Fordon", id: "fordon" },
  { to: "/salj", label: "Sälj till oss", id: "salj" },
  { to: "/om-oss", label: "Om oss", id: "om-oss" },
  { to: "/hitta-hit", label: "Hitta hit", id: "hitta-hit" },
  { to: "/kontakt", label: "Kontakt", id: "kontakt" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="glass border-b border-metallic/60">
        <nav className="mx-auto max-w-7xl px-5 lg:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3 shrink-0" onClick={() => setOpen(false)}>
            <img
              src={SITE.logo}
              alt="Borås Fritid logotyp"
              className="h-10 w-10 md:h-12 md:w-12 object-contain drop-shadow-[0_0_18px_rgba(37,99,235,0.55)]"
            />
            <span className="leading-tight">
              <span className="block font-display font-extrabold tracking-tight text-base md:text-lg text-white">
                BORÅS <span className="text-gradient-blue">FRITID</span>
              </span>
              <span className="block text-[10px] tracking-[0.28em] text-slate-400 font-mono-brand">
                KÖPER · SÄLJER · BYTER
              </span>
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-5">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `relative font-medium text-sm transition-colors hover:text-white ${
                    isActive || pathname.startsWith(l.to) ? "text-white" : "text-slate-400"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              title="Admin"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-metallic text-slate-400 hover:text-electric hover:border-electric/50 transition-colors"
            >
              <Lock size={15} />
            </Link>
            <a
              href={`tel:${SITE.phone}`}
              data-testid="nav-phone-button"
              className="flex items-center gap-2 rounded-full border border-metallic px-4 py-2 text-sm font-medium text-slate-200 hover:border-brand transition-colors"
            >
              <Phone size={15} className="text-electric" />
              {SITE.phoneDisplay}
            </a>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-metallic text-slate-200"
            aria-label="Meny"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </nav>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden glass border-b border-metallic/60"
          >
            <div className="px-5 py-5 flex flex-col gap-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => setOpen(false)}
                  className="font-display font-bold text-xl text-slate-100"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-2 flex flex-col gap-3">
                <a
                  href={`tel:${SITE.phone}`}
                  data-testid="nav-mobile-phone-link"
                  className="rounded-full border border-metallic px-5 py-3 text-center text-sm font-medium text-slate-200"
                >
                  Ring {SITE.phoneDisplay}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
